/**
 * @fileoverview System-web import template library grid and slideout surface.
 * @module system-web/import-templates-page
 *
 * Responsibilities:
 * - Render the local-first import template grid for system and department templates
 * - Hydrate slideout details from the full local Dexie template document
 * - Route row actions through service boundaries instead of direct API calls
 *
 * Dependencies:
 * - `../services/systemImportTemplateService` for template reads and mutations
 * - `../components/SystemImportTemplateSlideout` for detail rendering
 * - `@ui-admin/components/GridPageShell` for grid chrome and row actions
 *
 * Architecture:
 * - Grid rows come from Dexie-backed list services
 * - Slideout detail hydration upgrades list projections to full local documents
 * - Draft templates remain editable but are not runnable from the import-launch action
 *
 * Notes:
 * - Gate 3 templates must be published before the run wizard can preload mappings
 * - Department templates remain read-only from the system-web CRUD surface
 */

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Plus, Eye, Upload, Pencil, Copy, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { GridPageShell } from '@ui-admin/components/GridPageShell';
import type {
  DataGridColumn,
  DataGridFilter,
  DataGridRowAction,
  GridTemplate,
} from '@ui-admin/components/DataGrid';
import type { ImportDataMode, ImportBehavior, ImportTemplateVisibility } from '@firesquirrel/local-db';
import {
  listImportTemplates,
  getImportTemplate,
  cloneImportTemplate,
  deleteImportTemplate,
} from '../services/systemImportTemplateService';
import {
  SystemImportTemplateSlideout,
  BEHAVIOR_LABELS,
  DATA_MODE_COLORS,
  VISIBILITY_LABELS,
  VISIBILITY_COLORS,
  VISIBILITIES,
  formatDate,
  type SystemTemplateRow,
} from '../components/SystemImportTemplateSlideout';
import ConfirmDeleteModal from '../components/ui/ConfirmDeleteModal';

// ---------------------------------------------------------------------------
// Shell / Variant / Bound classification (Phase 3 — mirrors tenant-web)
// ---------------------------------------------------------------------------
type TemplateKind = 'draft' | 'shell' | 'variant' | 'bound';

function classifyTemplate(row: SystemTemplateRow): TemplateKind {
  // Draft wins. A half-saved template with empty mappings is in-progress
  // work, not a shell blueprint — shells are an intentional *published*
  // kind (deferred mappings). See importTemplate.ts for the distinction.
  if (row.isDraft === true || row.status === 'draft') return 'draft';
  if (row.parentTemplateId) return 'variant';
  if ((row.fieldMappingCount ?? 0) === 0) return 'shell';
  return 'bound';
}

const KIND_BADGE: Record<TemplateKind, { label: string; className: string }> = {
  draft:   { label: 'Draft',   className: 'bg-amber-50 text-amber-800 border border-amber-200' },
  shell:   { label: 'Shell',   className: 'bg-orange-50 text-orange-700 border border-orange-200' },
  bound:   { label: 'Bound',   className: 'bg-blue-50 text-blue-700 border border-blue-200' },
  variant: { label: 'Variant', className: 'bg-purple-50 text-purple-700 border border-purple-200' },
};

// ---------------------------------------------------------------------------
// Columns
// ---------------------------------------------------------------------------

// buildColumns — takes a shell-id→name lookup so variant rows can
// breadcrumb their parent shell in the Name column.
function buildColumns(
  shellNameById: Map<string, string>,
): DataGridColumn<SystemTemplateRow>[] {
  return [
  {
    id: 'name',
    header: 'Name',
    accessorKey: 'name',
    sortable: true,
    cell: (info) => {
      const row = info.row.original;
      const kind = classifyTemplate(row);
      const parentName = row.parentTemplateId ? shellNameById.get(row.parentTemplateId) : undefined;
      return (
        <div className={kind === 'variant' ? 'pl-4 border-l-2 border-purple-200 -ml-2' : ''}>
          <div className="font-medium text-gray-900 flex items-center gap-1.5">
            {kind === 'variant' && <span className="text-purple-400" aria-hidden>↳</span>}
            {row.name}
          </div>
          {kind === 'variant' && parentName && (
            <div className="text-[11px] text-purple-600 mt-0.5">
              variant of <span className="font-medium">{parentName}</span>
            </div>
          )}
          {row.description && (
            <div className="text-xs text-gray-400 mt-0.5 truncate max-w-xs">{row.description}</div>
          )}
        </div>
      );
    },
  },
  {
    id: 'templateKind',
    header: 'Type',
    accessorFn: (row) => classifyTemplate(row),
    width: 95,
    sortable: true,
    cell: (info) => {
      const kind = info.getValue() as TemplateKind;
      const badge = KIND_BADGE[kind];
      return (
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${badge.className}`}>
          {badge.label}
        </span>
      );
    },
  },
  {
    id: 'scope',
    header: 'Source',
    accessorKey: 'scope',
    width: 140,
    sortable: true,
    cell: (info) => {
      const row = info.row.original;
      if (row.scope === 'system') {
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-amber-50 text-amber-700 border border-amber-200">
            System
          </span>
        );
      }
      return (
        <div>
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-indigo-50 text-indigo-700 border border-indigo-200">
            Department
          </span>
          {row.tenantName && (
            <div className="text-[10px] text-gray-400 mt-0.5 truncate max-w-[120px]" title={row.tenantName}>
              {row.tenantName}
            </div>
          )}
        </div>
      );
    },
  },
  {
    id: 'dataMode',
    header: 'Data Mode',
    accessorKey: 'dataMode',
    width: 110,
    sortable: true,
    cell: (info) => {
      const mode = info.getValue() as ImportDataMode;
      return (
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${DATA_MODE_COLORS[mode] ?? DATA_MODE_COLORS.generic}`}>
          {mode}
        </span>
      );
    },
  },
  {
    id: 'importBehavior',
    header: 'Behavior',
    accessorKey: 'importBehavior',
    width: 110,
    sortable: true,
    cell: (info) => (
      <span className="text-xs text-gray-600">
        {BEHAVIOR_LABELS[info.getValue() as ImportBehavior] ?? String(info.getValue())}
      </span>
    ),
  },
  {
    id: 'visibility',
    header: 'Visibility',
    accessorKey: 'visibility',
    width: 140,
    sortable: true,
    cell: (info) => {
      const row = info.row.original;
      if (row.scope === 'department') return <span className="text-xs text-gray-400">—</span>;
      const v = info.getValue() as ImportTemplateVisibility;
      if (!v) return <span className="text-xs text-gray-400">—</span>;
      return (
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${VISIBILITY_COLORS[v]}`}>
          {VISIBILITY_LABELS[v]}
        </span>
      );
    },
  },
  {
    id: 'fieldMappingCount',
    header: 'Mappings',
    accessorKey: 'fieldMappingCount',
    width: 90,
    cell: (info) => (
      <span className="text-xs text-gray-600 tabular-nums">{String(info.getValue() ?? 0)}</span>
    ),
  },
  {
    id: 'updatedAt',
    header: 'Updated',
    accessorKey: 'updatedAt',
    width: 120,
    sortable: true,
    cell: (info) => (
      <span className="text-xs text-gray-500">{formatDate(info.getValue() as string | undefined)}</span>
    ),
  },
  ];
}

// ---------------------------------------------------------------------------
// Filters
// ---------------------------------------------------------------------------

const FILTERS: DataGridFilter[] = [
  {
    id: 'templateKind',
    label: 'Template Type',
    type: 'select',
    options: [
      { value: 'shell',   label: 'Shell' },
      { value: 'bound',   label: 'Bound' },
      { value: 'variant', label: 'Variant' },
    ],
  },
  {
    id: 'scope',
    label: 'Source',
    type: 'select',
    options: [
      { value: 'system',     label: 'System Templates' },
      { value: 'department', label: 'Department Templates' },
    ],
  },
  {
    id: 'dataMode',
    label: 'Data Mode',
    type: 'select',
    options: [
      { value: 'parcel',  label: 'Parcel' },
      { value: 'lookup',  label: 'Lookup' },
      { value: 'contact', label: 'Contact' },
      { value: 'generic', label: 'Generic' },
      { value: 'parcelOwnerUpdate', label: 'Ownership Update' },
    ],
  },
  {
    id: 'visibility',
    label: 'Visibility',
    type: 'select',
    options: VISIBILITIES.map(v => ({ value: v, label: VISIBILITY_LABELS[v] })),
  },
  {
    id: 'importBehavior',
    label: 'Behavior',
    type: 'select',
    options: [
      { value: 'upsert',    label: 'Upsert' },
      { value: 'insert',    label: 'Insert Only' },
      { value: 'append',    label: 'Append' },
      { value: 'full-sync', label: 'Full Sync' },
    ],
  },
];

// ---------------------------------------------------------------------------
// Seed grid templates
// ---------------------------------------------------------------------------

const NOW = new Date().toISOString();

const SEED_TEMPLATES: GridTemplate[] = [
  {
    _id: 'seed-sys-import-all',
    gridType: 'system-import-templates',
    name: 'All Templates',
    isDefault: true,
    scope: 'system',
    createdBy: { userId: 'system', displayName: 'System' },
    usageCount: 0,
    createdAt: NOW,
    updatedAt: NOW,
    state: {
      columnVisibility: {},
      columnOrder: ['name', 'templateKind', 'scope', 'dataMode', 'importBehavior', 'visibility', 'fieldMappingCount', 'updatedAt'],
      sorting: [{ id: 'updatedAt', desc: true }],
      filterValues: {},
      pageSize: 25,
      searchText: '',
    },
  },
  {
    _id: 'seed-sys-import-sysonly',
    gridType: 'system-import-templates',
    name: 'System Templates',
    isDefault: false,
    scope: 'system',
    createdBy: { userId: 'system', displayName: 'System' },
    usageCount: 0,
    createdAt: NOW,
    updatedAt: NOW,
    state: {
      columnVisibility: {},
      columnOrder: ['name', 'dataMode', 'importBehavior', 'visibility', 'fieldMappingCount', 'updatedAt'],
      sorting: [{ id: 'name', desc: false }],
      filterValues: { scope: 'system' },
      pageSize: 25,
      searchText: '',
    },
  },
  {
    _id: 'seed-sys-import-alldepts',
    gridType: 'system-import-templates',
    name: 'Shared with All Departments',
    isDefault: false,
    scope: 'system',
    createdBy: { userId: 'system', displayName: 'System' },
    usageCount: 0,
    createdAt: NOW,
    updatedAt: NOW,
    state: {
      columnVisibility: {},
      columnOrder: ['name', 'dataMode', 'importBehavior', 'fieldMappingCount', 'updatedAt'],
      sorting: [{ id: 'name', desc: false }],
      filterValues: { scope: 'system', visibility: 'all-departments' },
      pageSize: 25,
      searchText: '',
    },
  },
  {
    _id: 'seed-sys-import-depts',
    gridType: 'system-import-templates',
    name: 'Department Templates',
    isDefault: false,
    scope: 'system',
    createdBy: { userId: 'system', displayName: 'System' },
    usageCount: 0,
    createdAt: NOW,
    updatedAt: NOW,
    state: {
      columnVisibility: {},
      columnOrder: ['name', 'scope', 'dataMode', 'importBehavior', 'fieldMappingCount', 'updatedAt'],
      sorting: [{ id: 'updatedAt', desc: true }],
      filterValues: { scope: 'department' },
      pageSize: 25,
      searchText: '',
    },
  },
];

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

/**
 * @component ImportTemplatesPage
 * Purpose:     Renders the local-first import template library grid for system and
 *              department templates, with a slideout for detail viewing and row
 *              actions (view, edit, clone, delete, run).
 * Props:       (none — route component)
 * Behavior:    Loads templates via listImportTemplates; hydrates slideout details
 *              from the full local Dexie template document; routes row actions
 *              through service boundaries.
 * Data Source: local (Dexie via systemImportTemplateService)
 * Constraints: no direct API calls; all mutations through service functions
 * Performance: grid virtualized by GridPageShell; slideout hydrates on demand
 */
export default function ImportTemplatesPage() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<SystemTemplateRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState<SystemTemplateRow | null>(null);
  const [cloning, setCloning] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<SystemTemplateRow | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const items = await listImportTemplates();
      setTemplates(items as any[]);
    } catch {
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const openTemplateDetails = useCallback(async (row: SystemTemplateRow) => {
    setSelectedTemplate(row);
    try {
      const full = await getImportTemplate(row._id);
      if (!full) return;
      setSelectedTemplate((prev) => {
        if (!prev || prev._id !== row._id) return prev;
        return {
          ...prev,
          ...full,
          fieldMappings: full.fieldMappings,
          fieldMappingCount: Array.isArray(full.fieldMappings) ? full.fieldMappings.length : prev.fieldMappingCount,
          tenantName: prev.tenantName,
          tenantId: prev.tenantId,
        } as SystemTemplateRow;
      });
    } catch {
      // Keep the list projection visible if the full local document is unavailable.
    }
  }, []);

  const refreshSelectedTemplate = useCallback(async () => {
    if (!selectedTemplate) return;
    await openTemplateDetails(selectedTemplate);
  }, [openTemplateDetails, selectedTemplate]);

  useEffect(() => { load(); }, [load]);

  // Shell-id → name lookup so variant rows can breadcrumb their parent
  // in the Name column. Rebuilt whenever the template list changes.
  const shellNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const t of templates) {
      if (!t.parentTemplateId && (t.fieldMappingCount ?? 0) === 0) {
        m.set(t._id, t.name);
      }
    }
    return m;
  }, [templates]);

  const columns = useMemo(() => buildColumns(shellNameById), [shellNameById]);

  const handleEdit = useCallback((t: SystemTemplateRow) => {
    navigate(`/import-templates/${t._id}/edit`);
  }, [navigate]);

  const handleClone = useCallback(async (t: SystemTemplateRow) => {
    setCloning(true);
    try {
      await cloneImportTemplate(t._id);
      load();
    } finally {
      setCloning(false);
    }
  }, [load]);

  const handleDelete = useCallback((t: SystemTemplateRow) => {
    setDeleteTarget(t);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteImportTemplate(deleteTarget._id);
      if (selectedTemplate?._id === deleteTarget._id) setSelectedTemplate(null);
      setDeleteTarget(null);
      load();
    } finally {
      setDeleting(false);
    }
  }, [deleteTarget, load, selectedTemplate]);

  const rowActions: DataGridRowAction<SystemTemplateRow>[] = useMemo(() => [
    {
      id: 'view',
      label: 'View Details',
      icon: Eye,
      onClick: (row) => { void openTemplateDetails(row); },
    },
    {
      id: 'import',
      label: 'Import Data',
      icon: Upload,
      onClick: (row) => {
        const base = `/import-runs/new/${row._id}`;
        const qs = row.tenantId ? `?tenantId=${encodeURIComponent(row.tenantId)}` : '';
        navigate(`${base}${qs}`);
      },
      hidden: (row) => row?.isDraft === true || row?.status === 'draft',
    },
    {
      id: 'edit',
      label: 'Edit',
      icon: Pencil,
      onClick: (row) => handleEdit(row),
      hidden: (row) => row?.scope === 'department',
    },
    {
      id: 'clone',
      label: 'Clone',
      icon: Copy,
      onClick: (row) => handleClone(row),
    },
    {
      id: 'delete',
      label: 'Delete',
      icon: Trash2,
      variant: 'danger' as const,
      onClick: (row) => handleDelete(row),
      hidden: (row) => row?.scope === 'department',
    },
  ], [handleEdit, handleClone, handleDelete, navigate, openTemplateDetails]);

  const toolbarActions = (
    <button
      type="button"
      onClick={() => navigate('/import-templates/new')}
      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-orange-600 rounded-lg hover:bg-orange-700 transition-colors"
    >
      <Plus className="h-3.5 w-3.5" />
      New Template
    </button>
  );

  return (
    <>
      <GridPageShell<SystemTemplateRow>
        gridId="system-import-templates"
        gridType="system-import-templates"
        title="Import Templates"
        subtitle="System templates and all department templates across every tenant"
        columns={columns}
        data={templates}
        getRowId={(row) => row._id}
        rowActions={rowActions}
        filters={FILTERS}
        seedTemplates={SEED_TEMPLATES}
        onRowClick={(row) => { void openTemplateDetails(row); }}
        activeRowId={selectedTemplate?._id ?? null}
        toolbarActions={toolbarActions}
        searchPlaceholder="Search templates…"
        emptyIcon={Upload}
        emptyTitle="No import templates found"
        emptySubtitle="Create a system template or check that departments have created their own templates."
        emptyCTA={{ label: 'New System Template', onClick: () => navigate('/import-templates/new') }}
        panelContent={
          selectedTemplate ? (
            <SystemImportTemplateSlideout
              template={selectedTemplate}
              onClose={() => setSelectedTemplate(null)}
              onEdit={() => handleEdit(selectedTemplate)}
              onClone={() => handleClone(selectedTemplate)}
              onDelete={() => handleDelete(selectedTemplate)}
              onFullPage={() => {
                if (selectedTemplate.scope === 'system') {
                  navigate(`/import-templates/${selectedTemplate._id}/edit`);
                }
              }}
              cloning={cloning}
              deleting={deleting}
              onVisibilityChanged={() => {
                void load();
                void refreshSelectedTemplate();
              }}
            />
          ) : null
        }
        onPanelClose={() => setSelectedTemplate(null)}
        onRefresh={load}
      />
      {deleteTarget && (
        <ConfirmDeleteModal
          title="Delete Template?"
          confirmValue={deleteTarget.name}
          confirmLabel="template name"
          description={<>Are you sure you want to delete the template <strong>{deleteTarget.name}</strong>? This action cannot be undone.</>}
          deleteButtonLabel="Delete Template"
          isSubmitting={deleting}
          onConfirm={confirmDelete}
          onClose={() => setDeleteTarget(null)}
        />
      )}
    </>
  );
}
